import type { AdminUserDetail as AdminUserDetailData } from '@molt/shared';
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';

import { Avatar, profileName } from '@/components/Avatar';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { IconButton } from '@/components/IconButton';
import { Notice } from '@/components/Notice';
import { RelativeTime } from '@/components/RelativeTime';
import { TextField } from '@/components/TextField';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { AdminPill } from './AdminConsole';
import { AdminUserStatus } from './AdminUserTable';

/**
 * SA-9 — one account in the platform admin console (`GET /admin/users/:id`),
 * and the three account actions SA-8 serves.
 *
 * ---------------------------------------------------------------------------
 * THE WEB REFUSES WHAT THE SERVER WILL REFUSE, AND SAYS WHY
 * ---------------------------------------------------------------------------
 * `POST /admin/users/:id/{deactivate,reactivate,sessions/revoke}` answer 409 for
 * the caller's own account and for another platform admin, and 404 for a
 * soft-deleted one (D12). Those three are knowable from the detail, so each
 * action is disabled up front with the reason as `Button`'s `disabledReason`
 * (tooltip and `aria-describedby`). The server stays the enforcement: the
 * fourth refusal — D11's last-admin rule — is not knowable here, and arrives as
 * a 409 the route shows in `refusal`.
 *
 * Every action confirms through `Dialog` with a required reason, because the
 * reason is the audit row's only record of why (SA-11) and `confirm: true` is
 * the body's guard against a stray POST.
 */

export type AdminUserAction = 'deactivate' | 'reactivate' | 'revoke';

const ACTION_KEYS: Readonly<
  Record<
    AdminUserAction,
    { trigger: string; title: string; body: string; confirm: string }
  >
> = {
  deactivate: {
    trigger: 'admin.user.deactivate',
    title: 'admin.user.confirm.deactivateTitle',
    body: 'admin.user.confirm.deactivateBody',
    confirm: 'admin.user.confirm.deactivate',
  },
  reactivate: {
    trigger: 'admin.user.reactivate',
    title: 'admin.user.confirm.reactivateTitle',
    body: 'admin.user.confirm.reactivateBody',
    confirm: 'admin.user.confirm.reactivate',
  },
  revoke: {
    trigger: 'admin.user.revokeSessions',
    title: 'admin.user.confirm.revokeTitle',
    body: 'admin.user.confirm.revokeBody',
    confirm: 'admin.user.confirm.revoke',
  },
};

const ROLE_KEYS: Readonly<Record<'admin' | 'normal' | 'observer' | 'member', string>> = {
  admin: 'admin.role.admin',
  normal: 'admin.role.normal',
  observer: 'admin.role.observer',
  member: 'admin.role.member',
};

/**
 * Why the actions are refused before any request, or `null` when they are not.
 * Deleted outranks the other two: there is no account to act on at all.
 */
export function adminUserActionRefusalKey(
  user: Pick<AdminUserDetailData, 'id' | 'isSuper' | 'deletedAt'>,
  viewerUserId: string,
): string | null {
  if (user.deletedAt !== null) return 'admin.user.refused.deleted';
  if (user.id === viewerUserId) return 'admin.user.refused.self';
  if (user.isSuper) return 'admin.user.refused.superadmin';
  return null;
}

export interface AdminUserDetailProps {
  readonly user: AdminUserDetailData;
  /** The signed-in superadmin — their own account is refused. */
  readonly viewerUserId: string;
  /** The act in flight, if any. */
  readonly pendingAction: AdminUserAction | null;
  /** The server's refusal of the last act (a 409), shown until the next attempt. */
  readonly refusal: string | null;
  /**
   * Performs the act. Resolves when it has settled either way; the dialog
   * closes then, and a refusal arrives through `refusal`.
   */
  readonly onConfirm: (action: AdminUserAction, reason: string) => Promise<void>;
  /**
   * The "Avatar initials" control (member initials,
   * `docs/notes/member-initials.md`), rendered right after the facts so it sits
   * beside the avatar it changes. A slot for the same reason as `children`: the
   * route owns the mutation (`AdminUserInitials`), and this component stays
   * presentational.
   */
  readonly initials?: ReactNode;
  /**
   * Further sections, rendered after Memberships at the page's own width and
   * gap — B85's `ApiTokensAdminSection`. A slot rather than an import, so this
   * component stays presentational and its tests need no token fetch.
   */
  readonly children?: ReactNode;
}

export function AdminUserDetail({
  user,
  viewerUserId,
  pendingAction,
  refusal,
  onConfirm,
  initials,
  children,
}: AdminUserDetailProps) {
  const { t } = useInterfaceLanguage();
  const name = profileName(user);
  const [openAction, setOpenAction] = useState<AdminUserAction | null>(null);
  const [reason, setReason] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const refusalKey = adminUserActionRefusalKey(user, viewerUserId);
  const disabledReason = refusalKey === null ? undefined : t(refusalKey);
  const lifecycleAction: AdminUserAction =
    user.deactivatedAt === null ? 'deactivate' : 'reactivate';
  const { memberships } = user;

  function open(action: AdminUserAction) {
    setReason('');
    setOpenAction(action);
  }

  async function confirm() {
    if (openAction === null || reason.trim() === '') return;
    await onConfirm(openAction, reason.trim());
    setOpenAction(null);
  }

  const keys = openAction === null ? null : ACTION_KEYS[openAction];

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 py-2">
      <Link
        to="/admin"
        className="self-start text-sm font-medium text-accent underline underline-offset-2"
      >
        {t('admin.user.back')}
      </Link>

      <header className="flex min-w-0 flex-wrap items-center gap-4">
        <Avatar profile={user} size="md" decorative />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-xs font-medium uppercase text-ink-muted">
            {t('admin.user.eyebrow')}
          </p>
          <h1 className="break-words font-display text-3xl font-semibold text-ink">
            {name}
          </h1>
          <AdminUserStatus user={user} />
        </div>
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-card border border-border-strong bg-surface-raised p-4 sm:grid-cols-2">
        <Fact label={t('admin.user.field.email')}>{user.email}</Fact>
        <Fact label={t('admin.user.field.username')}>
          {user.username === '' ? '—' : `@${user.username}`}
        </Fact>
        <Fact label={t('admin.user.field.id')}>
          <span className="font-mono text-xs">{user.id}</span>
        </Fact>
        <Fact label={t('admin.user.field.created')}>
          <RelativeTime value={user.createdAt} format="long" />
        </Fact>
      </dl>

      {initials}

      <section aria-labelledby="admin-user-actions" className="flex flex-col gap-3">
        <h2 id="admin-user-actions" className="text-lg font-semibold text-ink">
          {t('admin.user.actions')}
        </h2>
        {refusal === null ? null : (
          <Notice tone="bad" title={t('admin.user.refusedTitle')} body={refusal} />
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={lifecycleAction === 'deactivate' ? 'danger' : 'primary'}
            disabled={refusalKey !== null || pendingAction !== null}
            disabledReason={disabledReason}
            onClick={() => open(lifecycleAction)}
          >
            {t(ACTION_KEYS[lifecycleAction].trigger)}
          </Button>
          <Button
            type="button"
            disabled={refusalKey !== null || pendingAction !== null}
            disabledReason={disabledReason}
            onClick={() => open('revoke')}
          >
            {t(ACTION_KEYS.revoke.trigger)}
          </Button>
        </div>
        <Link
          to="/admin"
          search={{ tab: 'audit', target: user.id }}
          className="self-start text-sm font-medium text-accent underline underline-offset-2"
        >
          {t('admin.user.viewAudit')}
        </Link>
      </section>

      <section aria-labelledby="admin-user-memberships" className="flex flex-col gap-4">
        <h2 id="admin-user-memberships" className="text-lg font-semibold text-ink">
          {t('admin.user.memberships')}
        </h2>

        <MembershipGroup
          heading={t(
            'admin.user.workspaces',
            { count: memberships.counts.workspaces },
            memberships.counts.workspaces,
          )}
          shown={memberships.workspaces.length}
          count={memberships.counts.workspaces}
        >
          {memberships.workspaces.map((workspace) => (
            <MembershipRow key={workspace.id} role={t(ROLE_KEYS[workspace.role])}>
              <span className="font-medium text-ink">{workspace.name}</span>
            </MembershipRow>
          ))}
        </MembershipGroup>

        <MembershipGroup
          heading={t(
            'admin.user.boards',
            { count: memberships.counts.boards },
            memberships.counts.boards,
          )}
          shown={memberships.boards.length}
          count={memberships.counts.boards}
        >
          {memberships.boards.map((board) => (
            <MembershipRow
              key={board.id}
              role={t(ROLE_KEYS[board.role])}
              extra={
                board.closed ? (
                  <AdminPill tone="warn">{t('admin.boards.status.closed')}</AdminPill>
                ) : null
              }
            >
              <Link
                to="/b/$shortLink/{-$slug}"
                params={{ shortLink: board.shortLink }}
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                {board.title}
              </Link>
            </MembershipRow>
          ))}
        </MembershipGroup>

        <MembershipGroup
          heading={t(
            'admin.user.enterprises',
            { count: memberships.counts.enterprises },
            memberships.counts.enterprises,
          )}
          shown={memberships.enterprises.length}
          count={memberships.counts.enterprises}
        >
          {memberships.enterprises.map((enterprise) => (
            <MembershipRow key={enterprise.id} role={t(ROLE_KEYS[enterprise.role])}>
              <span className="font-medium text-ink">{enterprise.name}</span>
            </MembershipRow>
          ))}
        </MembershipGroup>
      </section>

      {children}

      <Dialog
        open={openAction !== null}
        onOpenChange={(next) => {
          if (!next) setOpenAction(null);
        }}
        size="sm"
        title={keys === null ? '' : t(keys.title, { name })}
        description={keys === null ? undefined : t(keys.body)}
        initialFocus={reasonRef}
        closeControl={
          <IconButton label={t('admin.user.confirm.close')} variant="quiet" size="sm">
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          <>
            <Button type="button" variant="quiet" onClick={() => setOpenAction(null)}>
              {t('admin.user.confirm.cancel')}
            </Button>
            <Button
              type="submit"
              form="admin-user-action-form"
              variant={openAction === 'reactivate' ? 'primary' : 'danger'}
              disabled={reason.trim() === ''}
              busy={pendingAction !== null}
              busyLabel={t('admin.user.confirm.working')}
            >
              {keys === null ? '' : t(keys.confirm)}
            </Button>
          </>
        }
      >
        <form
          id="admin-user-action-form"
          onSubmit={(event) => {
            event.preventDefault();
            void confirm();
          }}
        >
          <TextField
            ref={reasonRef}
            multiline
            required
            label={t('admin.user.confirm.reasonLabel')}
            hint={t('admin.user.confirm.reasonHint')}
            value={reason}
            maxLength={1000}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        </form>
      </Dialog>
    </div>
  );
}

function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase text-ink-muted">{label}</dt>
      <dd className="break-words text-card text-ink">{children}</dd>
    </div>
  );
}

function MembershipGroup({
  heading,
  shown,
  count,
  children,
}: {
  readonly heading: string;
  readonly shown: number;
  readonly count: number;
  readonly children: ReactNode;
}) {
  const { t } = useInterfaceLanguage();
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-ink">{heading}</h3>
      {count === 0 ? (
        <p className="text-sm text-ink-muted">{t('admin.user.noMemberships')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-card border border-border-strong bg-surface">
          {children}
        </ul>
      )}
      {shown < count ? (
        <p className="text-xs text-ink-muted">
          {t('admin.user.truncated', { shown, count })}
        </p>
      ) : null}
    </div>
  );
}

function MembershipRow({
  role,
  extra,
  children,
}: {
  readonly role: string;
  readonly extra?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-card">
      <span className="min-w-0 break-words">{children}</span>
      <span className="flex shrink-0 items-center gap-2">
        {extra}
        <AdminPill>{role}</AdminPill>
      </span>
    </li>
  );
}
